/**
 * Termos de Uso do app Meiuca (consumidor final).
 *
 *  RASCUNHO PRELIMINAR — REVISÃO JURÍDICA OBRIGATÓRIA antes da publicação oficial.
 *
 * Inclui a cláusula obrigatória do Playbook BaaS Asaas (slide 12) que descreve
 * o ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A. como prestador dos
 * serviços financeiros, em conformidade com a Resolução Conjunta nº 16/2025 do
 * Banco Central do Brasil.
 *
 * Endpoint público esperado: https://meiucaapp.com.br/termos
 *
 * Migrado de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

export function TermosPage() {
  return (
    <LegalPageLayout
      title="Termos de Uso"
      documentTitle="Termos de Uso — Meiuca"
      lastUpdated="20 de maio de 2026"
    >
      <Body>
        Estes Termos de Uso regulam o acesso e uso do aplicativo Meiuca. Ao criar uma
        conta ou utilizar o app, você declara que leu, entendeu e concorda integralmente
        com estas condições. Caso não concorde, não use o aplicativo.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">1. Sobre o serviço</H2>
        <Body>
          O Meiuca é uma plataforma que conecta praticantes esportivos a arenas, centros
          de treinamento e profissionais parceiros, permitindo a busca, reserva e pagamento
          de horários, planos e atividades esportivas. O Meiuca atua como{' '}
          <strong>integradora tecnológica e distribuidora da experiência do produto</strong>
          {' '}— a prestação efetiva do serviço esportivo é responsabilidade da arena, centro
          de treinamento ou parceiro escolhido por você.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">2. Cadastro</H2>
        <Body>
          Para usar o Meiuca, você precisa ser maior de 18 anos (ou ter 13 anos ou mais
          com consentimento de responsável legal) e fornecer dados verdadeiros, exatos
          e atualizados. Você é responsável por manter a confidencialidade da sua senha
          e por todas as ações realizadas com sua conta. Notifique-nos imediatamente em{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>{' '}
          em caso de uso não autorizado da sua conta.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">3. Reservas e atividades</H2>
        <Body>
          Ao reservar uma quadra, plano, day use ou aula, você celebra uma relação direta
          com a arena, centro de treinamento ou profissional responsável pela atividade.
          O valor cobrado, política de cancelamento, reembolso e regras específicas são
          definidos pelo parceiro responsável — verifique-os antes de confirmar a reserva.
        </Body>
        <Body className="mt-3">
          A reserva só é considerada confirmada após a aprovação do pagamento, conforme
          a cláusula 4 abaixo.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          4. Prestação dos serviços financeiros (BaaS)
        </H2>
        <Body>
          Os serviços financeiros e de pagamentos disponibilizados por meio do aplicativo
          Meiuca — incluindo emissão de Pix para reservas, processamento de pagamentos,
          confirmação de transações e demais movimentações de valores — são prestados pelo{' '}
          <strong>
            ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
          </strong>
          , inscrito no CNPJ sob nº <strong>19.540.550/0001-21</strong>, instituição de
          pagamento autorizada a funcionar pelo Banco Central do Brasil (BACEN), nos termos
          da Resolução BCB nº 80/2021 e demais normas aplicáveis.
        </Body>
        <Body className="mt-3">
          A Meiuca atua exclusivamente como <strong>integradora tecnológica e distribuidora
          da experiência do produto</strong>, não sendo instituição financeira ou de pagamento,
          nem realizando intermediação financeira em nome próprio. A Meiuca não detém,
          custodia ou movimenta os valores pagos: o Pix é creditado <strong>diretamente</strong>
          {' '}na subconta da arena, centro de treinamento ou parceiro responsável pela
          atividade, mantida no ASAAS.
        </Body>
        <Body className="mt-3">
          Você declara ciência de que o relacionamento de pagamentos e a responsabilidade
          regulatória pelos serviços financeiros descritos nesta cláusula são do ASAAS
          GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A., nos termos da regulamentação
          vigente — especialmente a Resolução Conjunta nº 16/2025 do BACEN.
        </Body>
        <Body className="mt-3">
          Para dúvidas, suporte ou reclamações relacionadas ao processamento financeiro
          (pagamento não identificado, recibo, estorno, etc.), você pode contatar diretamente
          o ASAAS pelos canais: <strong>0800 009 0037</strong> (somente pessoa jurídica) e{' '}
          <a
            href="mailto:contato@asaas.com.br"
            className="text-brand-primary hover:underline"
          >
            contato@asaas.com.br
          </a>.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">5. Cancelamento e reembolso</H2>
        <Body>
          As regras de cancelamento e reembolso são definidas pela arena, centro de
          treinamento ou parceiro responsável pela atividade e ficam visíveis no momento
          da reserva. Quando aplicável, o reembolso (parcial ou total) é processado pelo
          ASAAS via Pix, de volta ao mesmo método e identificador usados no pagamento.
        </Body>
        <Body className="mt-3">
          Em caso de divergência com o parceiro, fale primeiro com ele; o Meiuca pode
          ser acionado para mediar quando aplicável.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">6. Uso adequado</H2>
        <Body>Você concorda em não:</Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Usar o app para fins ilegais, fraudulentos ou que violem direitos de terceiros.</li>
          <li>Acessar áreas restritas, tentar burlar autenticação ou fazer engenharia reversa.</li>
          <li>Criar contas falsas ou se passar por outra pessoa.</li>
          <li>Sobrecarregar a infraestrutura por automações não autorizadas.</li>
          <li>Reproduzir, distribuir ou modificar conteúdo do app sem autorização.</li>
        </ul>
        <Body className="mt-4">
          Violar essas regras pode resultar em suspensão ou exclusão da sua conta, sem
          prejuízo de medidas legais cabíveis.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">7. Conteúdo e propriedade intelectual</H2>
        <Body>
          Todo o conteúdo do app — incluindo marca, logotipos, layout, código e textos —
          é de propriedade do Meiuca ou de seus licenciantes. Nenhum direito é transferido
          a você além da licença pessoal e revogável de uso do aplicativo dentro destes Termos.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">8. Limitação de responsabilidade</H2>
        <Body>
          O Meiuca se esforça para manter o serviço disponível e seguro, mas não garante
          ausência de falhas, indisponibilidades ou erros. Na máxima extensão permitida
          pela legislação aplicável, o Meiuca não se responsabiliza por:
        </Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Conduta de arenas, centros de treinamento, profissionais parceiros ou outros usuários.</li>
          <li>Lesões, danos ou prejuízos decorrentes da atividade esportiva.</li>
          <li>Atrasos, indisponibilidades ou erros na rede de telefonia ou internet.</li>
          <li>
            Decisões, falhas ou indisponibilidade do ASAAS (instituição financeira responsável
            pelo processamento dos pagamentos), nem por eventos fora do nosso controle razoável.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">9. Privacidade</H2>
        <Body>
          O tratamento dos seus dados pessoais é descrito na nossa{' '}
          <a href="/privacidade" className="text-brand-primary hover:underline">
            Política de Privacidade
          </a>
          , que faz parte integrante destes Termos. Para excluir sua conta, consulte{' '}
          <a href="/excluir-conta" className="text-brand-primary hover:underline">
            esta página
          </a>.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">10. Modificações</H2>
        <Body>
          Podemos atualizar estes Termos a qualquer momento. Mudanças relevantes serão
          comunicadas por aviso no app ou por mensagem ao seu telefone cadastrado.
          O uso continuado após a notificação implica concordância com a nova versão.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">11. Encerramento</H2>
        <Body>
          Você pode encerrar sua conta a qualquer momento — veja{' '}
          <a href="/excluir-conta" className="text-brand-primary hover:underline">
            como excluir
          </a>
          . O Meiuca pode suspender ou encerrar sua conta em caso de violação destes
          Termos, fraude, uso indevido ou exigência legal, com aviso prévio sempre que possível.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">12. Lei e foro</H2>
        <Body>
          Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica
          eleito o foro da comarca da sede do Meiuca para dirimir qualquer controvérsia,
          ressalvado o direito do consumidor de eleger o foro do seu domicílio.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">13. Contato</H2>
        <Body>
          Dúvidas, sugestões ou reclamações sobre o app:{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>.
        </Body>
        <Body className="mt-3">
          Para assuntos financeiros (Pix, recibo, estorno), contato direto com o ASAAS:{' '}
          <strong>0800 009 0037</strong> ou{' '}
          <a
            href="mailto:contato@asaas.com.br"
            className="text-brand-primary hover:underline"
          >
            contato@asaas.com.br
          </a>.
        </Body>
      </section>
    </LegalPageLayout>
  )
}
